import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.css'],
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  @ViewChild('editorArea') editorArea!: ElementRef<HTMLDivElement>;

  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() minHeight: string = '120px';
  @Input() value: string = '';

  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();

  public isBold = false;
  public isItalic = false;
  public isUnderline = false;
  public isStrike = false;
  public isBulletList = false;
  public isOrderedList = false;
  public isAlignLeft = false;
  public isAlignCenter = false;
  public isAlignRight = false;
  public isAlignJustify = false;

  public showTextColorPicker = false;
  public showBgColorPicker = false;

  public readonly colorPaletteRow1: string[] = [
    '#b60205', '#d93f0b', '#fbca04', '#0e8a16', '#006b75', '#1d76db', '#0052cc', '#5319e7'
  ];

  public readonly colorPaletteRow2: string[] = [
    '#e99695', '#f9d0c4', '#fef2c0', '#c2e0c6', '#bfdadc', '#c5def5', '#bfd4f2', '#d4c5f9'
  ];

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};
  private innerHtmlValue: string = '';
  private savedRange: Range | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.rte-color-wrapper')) {
      this.showTextColorPicker = false;
      this.showBgColorPicker = false;
    }
  }

  ngAfterViewInit(): void {
    this.updateContent(this.innerHtmlValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].isFirstChange()) {
      const newVal = changes['value'].currentValue || '';
      if (newVal !== this.innerHtmlValue) {
        this.innerHtmlValue = newVal;
        this.updateContent(newVal);
      }
    }
  }

  // --- ControlValueAccessor Implementation ---
  writeValue(val: any): void {
    const safeVal = val === null || val === undefined ? '' : String(val);
    this.innerHtmlValue = safeVal;
    if (this.editorArea?.nativeElement) {
      this.updateContent(safeVal);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Content and Event Handling ---
  private updateContent(html: string): void {
    if (!this.editorArea?.nativeElement) return;
    if (this.editorArea.nativeElement.innerHTML !== html) {
      this.editorArea.nativeElement.innerHTML = html;
    }
    this.updateToolbarState();
  }

  public onInput(): void {
    if (!this.editorArea?.nativeElement) return;
    let html = this.editorArea.nativeElement.innerHTML;

    // Standardize empty content
    if (html === '<p><br></p>' || html === '<br>' || html === '<div><br></div>') {
      html = '';
    }

    this.innerHtmlValue = html;
    this.onChange(html);
    this.valueChange.emit(html);
    this.updateToolbarState();
  }

  public onBlur(): void {
    this.onTouched();
    this.updateToolbarState();
  }

  public onKeydown(event: KeyboardEvent): void {
    if (this.disabled || event.isComposing) return;
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (event.key === ' ') {
      this.tryAutoFormatList(event);
    } else if (event.key === 'Enter') {
      this.tryAutoFormatListOnEnter(event);
    }
  }

  // Wandelt "1. ", "- " oder "* " am Zeilenanfang automatisch in eine Liste um (wie bei Quill)
  private tryAutoFormatList(event: KeyboardEvent): void {
    const ctx = this.getCaretBlock();
    if (!ctx) return;

    const prefixRange = document.createRange();
    prefixRange.setStart(ctx.block, 0);
    prefixRange.setEnd(ctx.caret.startContainer, ctx.caret.startOffset);
    const prefix = prefixRange.toString().replace(/ /g, ' ');

    const command = this.getListCommand(prefix, /^\s*(\d+\.|[-*])$/);
    if (!command) return;

    event.preventDefault();
    ctx.sel.removeAllRanges();
    ctx.sel.addRange(prefixRange);
    document.execCommand('delete');
    document.execCommand(command);
    this.onInput();
  }

  // Zeile "- Text" / "1. Text" + Enter: Zeile in Liste umwandeln und neuen Listenpunkt beginnen
  private tryAutoFormatListOnEnter(event: KeyboardEvent): void {
    const ctx = this.getCaretBlock();
    if (!ctx) return;

    const afterRange = document.createRange();
    afterRange.selectNodeContents(ctx.block);
    afterRange.setStart(ctx.caret.startContainer, ctx.caret.startOffset);
    if (afterRange.toString().trim() !== '') return;

    const beforeRange = document.createRange();
    beforeRange.setStart(ctx.block, 0);
    beforeRange.setEnd(ctx.caret.startContainer, ctx.caret.startOffset);
    const text = beforeRange.toString().replace(/ /g, ' ');

    const match = /^(\s*(\d+\.|[-*]) +)\S/.exec(text);
    if (!match) return;
    const command = this.getListCommand(match[2], /^(\d+\.|[-*])$/);
    const markerRange = this.getRangeAtTextOffsets(ctx.block, 0, match[1].length);
    if (!command || !markerRange) return;

    event.preventDefault();
    ctx.sel.removeAllRanges();
    ctx.sel.addRange(markerRange);
    document.execCommand('delete');
    document.execCommand(command);

    const li = ctx.sel.anchorNode ? (ctx.sel.anchorNode instanceof Element ? ctx.sel.anchorNode : ctx.sel.anchorNode.parentElement)?.closest('li') : null;
    if (li) {
      const end = document.createRange();
      end.selectNodeContents(li);
      end.collapse(false);
      ctx.sel.removeAllRanges();
      ctx.sel.addRange(end);
      document.execCommand('insertParagraph');
    }
    this.onInput();
  }

  private getListCommand(marker: string, pattern: RegExp): 'insertOrderedList' | 'insertUnorderedList' | null {
    const trimmed = marker.trim();
    if (!pattern.test(marker)) return null;
    return /^\d+\.$/.test(trimmed) ? 'insertOrderedList' : 'insertUnorderedList';
  }

  // Liefert den Block (Absatz/Zeile) um den Cursor, sofern dieser nicht bereits in einer Liste steht
  private getCaretBlock(): { block: Node; caret: Range; sel: Selection } | null {
    const root = this.editorArea?.nativeElement;
    const sel = window.getSelection();
    if (!root || !sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;

    const caret = sel.getRangeAt(0);
    if (!root.contains(caret.startContainer)) return null;

    let block: Node = caret.startContainer;
    while (block !== root && !(block instanceof HTMLElement && /^(P|DIV|H[1-6]|LI|BLOCKQUOTE)$/.test(block.tagName))) {
      block = block.parentNode as Node;
    }
    if (block instanceof HTMLElement && block.closest('li') && root.contains(block.closest('li'))) return null;

    return { block, caret, sel };
  }

  private getRangeAtTextOffsets(container: Node, start: number, end: number): Range | null {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    let pos = 0;
    let startSet = false;
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const len = (node.textContent ?? '').length;
      if (!startSet && start <= pos + len) {
        range.setStart(node, start - pos);
        startSet = true;
      }
      if (startSet && end <= pos + len) {
        range.setEnd(node, end - pos);
        return range;
      }
      pos += len;
    }
    return null;
  }

  public onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    document.execCommand('insertText', false, text);
  }

  // --- Selection Preservation for Color Popups ---
  private saveSelection(): void {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  private restoreSelection(): void {
    if (this.savedRange) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(this.savedRange);
      }
    }
  }

  public toggleTextColorPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.saveSelection();
    this.showTextColorPicker = !this.showTextColorPicker;
    this.showBgColorPicker = false;
  }

  public toggleBgColorPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.saveSelection();
    this.showBgColorPicker = !this.showBgColorPicker;
    this.showTextColorPicker = false;
  }

  public applyColor(command: 'foreColor' | 'hiliteColor', color: string): void {
    this.editorArea?.nativeElement.focus();
    this.restoreSelection();
    document.execCommand(command, false, color);
    this.showTextColorPicker = false;
    this.showBgColorPicker = false;
    this.onInput();
    this.updateToolbarState();
  }

  // --- Formatting Commands ---
  public executeCommand(command: string, arg?: string): void {
    if (this.disabled) return;
    this.editorArea?.nativeElement.focus();
    document.execCommand(command, false, arg ?? undefined);
    this.onInput();
    this.updateToolbarState();
  }

  public updateToolbarState(): void {
    try {
      this.isBold = document.queryCommandState('bold');
      this.isItalic = document.queryCommandState('italic');
      this.isUnderline = document.queryCommandState('underline');
      this.isStrike = document.queryCommandState('strikeThrough');
      this.isBulletList = document.queryCommandState('insertUnorderedList');
      this.isOrderedList = document.queryCommandState('insertOrderedList');
      this.isAlignLeft = document.queryCommandState('justifyLeft');
      this.isAlignCenter = document.queryCommandState('justifyCenter');
      this.isAlignRight = document.queryCommandState('justifyRight');
      this.isAlignJustify = document.queryCommandState('justifyFull');
    } catch {
      // queryCommandState can throw if selection is outside
    }
  }
}
